import { createContext, useCallback, useContext, useRef, useState } from "react";

/**
 * Global confirm() dialog — usage:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Save voucher?", body: "₹12,500 cash receipt — proceed?" }))) return;
 *   await api.post(...);
 *
 * Resolves to `true` when the user clicks Confirm, `false` on Cancel / backdrop.
 * Single dialog instance lives in <ConfirmProvider> at the app root; sequential
 * calls queue automatically because the previous promise must resolve first.
 */
const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: "", body: "", confirmLabel: "Confirm", cancelLabel: "Cancel", tone: "primary" });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        body: opts.body || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        tone: opts.tone || "primary",
      });
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="confirm-dialog">
          <div className="absolute inset-0 bg-ink/50" onClick={() => close(false)} />
          <div className="relative bg-canvas-card border border-canvas-line max-w-md w-[92%] mx-4 p-6 shadow-2xl">
            <div className="font-display text-lg font-bold text-ink mb-2">{state.title}</div>
            {state.body && <div className="text-sm text-ink-muted leading-relaxed whitespace-pre-line">{state.body}</div>}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                data-testid="confirm-dialog-cancel"
                className="px-4 py-2 border border-canvas-line text-sm hover:border-ink"
              >
                {state.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                data-testid="confirm-dialog-confirm"
                className={
                  state.tone === "danger"
                    ? "px-4 py-2 bg-destructive text-white text-sm font-semibold hover:opacity-90"
                    : "px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand"
                }
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
